import { Annotation, Canvas, Storyboard, SubCanvas } from "@/utils/types";
import { AnyAction, createSlice, Dispatch } from "@reduxjs/toolkit";
import { Database } from "@/utils/supabase.types";

type ActivityStream = Database["public"]["Tables"]["activity_stream"]["Row"];

export const supabaseSlice = createSlice({
  name: "supabaseStore",
  initialState: {
    // The list is always sorted by timestamp descending
    activityStreamList: [] as ActivityStream[],
    activityStreamLookup: {} as Record<string, ActivityStream>,
    activityStreamLoadStatus: "idle",

    // Diff's are looked up based on the activity ID (equal to ID)
    activityStreamDiffs: {},
    activityStreamDiffsLoadStatus: "idle",

    // Project loading statuses
    projectLoadingProgress: null,
    projectLoadingProgressLoadStatus: "idle",

    projectIdsByLastUpdated: [],
    projectIdsByLastUpdatedLoadStatus: "idle",
    projects: {},
    projectsLoadStatus: "idle",

    canvasPreviews: {},

    canvases: {},
    canvasesLoadStatus: "idle",
    ignoreCanvasUpdates: false,

    subCanvases: {},
    subCanvasesLoadStatus: "idle",

    elementMetadata: {},
    elementMetadataLoadStatus: "idle",

    // attached figma context by canvas id
    figmaContext: {},
    dsPrompt: "",
  },

  //@ts-expect-error TS2589
  reducers: {
    setDSPrompt: (state, action) => {
      if (action.payload?.prompt) {
        state.dsPrompt = action.payload.prompt;
      } else {
        state.dsPrompt = "";
      }
    },
    onDSPromptSnapshot: (state, action) => {
      state.dsPrompt = "";
      state.dsPrompt = action.payload || "";
    },
    setFigmaContext: (state, action) => {
      state.figmaContext = action.payload;
    },
    onFigmaContextSnapshot: (state, action) => {
      state.figmaContext = {};
      state.figmaContext = action.payload || {};
    },
    setElementMetadata: (state, action) => {
      state.elementMetadata = {};

      action.payload?.forEach((metadata: { hash: string }) => {
        state.elementMetadata[metadata.hash] = metadata;
      });
      state.elementMetadataLoadStatus = "succeeded";
    },
    onElementMetadataSnapshot: (state, action) => {
      action.payload?.newItems?.forEach((metadata: { hash: string }) => {
        state.elementMetadata[metadata.hash] = metadata;
      });

      action.payload?.deletedItems?.forEach((metadata: { hash: string }) => {
        delete state.elementMetadata[metadata.hash];
      });
    },
    setElementMetadataLoading: (state) => {
      state.elementMetadataLoadStatus = "loading";
    },
    setCanvases: (state, action) => {
      state.canvases = {};

      action.payload?.forEach((canvas) => {
        if (
          state.canvases?.[canvas.id]?.storyboards?.length &&
          !canvas?.storyboards?.length
        ) {
          canvas.storyboards = state.canvases[canvas.id].storyboards;
        }

        if (
          state.canvases?.[canvas.id]?.annotations?.length &&
          !canvas?.annotations?.length
        ) {
          canvas.annotations = state.canvases[canvas.id].annotations;
        }

        state.canvases[canvas.id] = canvas;
      });
      state.canvasesLoadStatus = "succeeded";
    },
    setIgnoreCanvasUpdates: (state, action) => {
      state.ignoreCanvasUpdates = action.payload;
    },
    setCanvasPreviews: (state, action) => {
      action.payload?.forEach((canvas) => {
        state.canvasPreviews[canvas.id] = canvas;
      });
    },
    onCanvasesSnapshot: (state, action) => {
      if (state.ignoreCanvasUpdates && !action.payload?.forceUpdate) {
        return;
      }

      action.payload?.newItems?.forEach((canvas) => {
        const lastUpdated = state.canvases[canvas.id]?.last_updated;
        if (
          lastUpdated &&
          new Date(lastUpdated) > new Date(canvas.last_updated)
        ) {
          return;
        }

        // Do not delete the storyboards as it can sometimes get too big and not come back
        // in the snapshots
        if (
          state.canvases?.[canvas.id]?.storyboards?.length &&
          !canvas?.storyboards?.length
        ) {
          canvas.storyboards = state.canvases[canvas.id].storyboards;
        }

        // Do not delete the annotations as it can sometimes get too big and not come back
        // in the snapshots
        if (
          state.canvases?.[canvas.id]?.annotations?.length &&
          !canvas?.annotations?.length
        ) {
          canvas.annotations = state.canvases[canvas.id].annotations;
        }

        state.canvases[canvas.id] = canvas;
      });

      action.payload?.deletedItems?.forEach((canvas) => {
        delete state.canvases[canvas.id];
      });
    },
    setCanvasesLoading: (state) => {
      state.canvasesLoadStatus = "loading";
    },
    setSubCanvases: (state, action) => {
      state.subCanvases = {};

      action.payload?.forEach((canvas) => {
        if (
          state.subCanvases?.[canvas.id]?.storyboards?.length &&
          !canvas?.storyboards?.length
        ) {
          canvas.storyboards = state.subCanvases[canvas.id].storyboards;
        }

        if (
          state.subCanvases?.[canvas.id]?.annotations?.length &&
          !canvas?.annotations?.length
        ) {
          canvas.annotations = state.subCanvases[canvas.id].annotations;
        }

        state.subCanvases[canvas.id] = canvas;
      });
      state.subCanvasesLoadStatus = "succeeded";
    },
    onSubCanvasesSnapshot: (state, action) => {
      if (state.ignoreCanvasUpdates && !action.payload?.forceUpdate) {
        return;
      }

      action.payload?.newItems?.forEach((subcanvas) => {
        const lastUpdated = state.subCanvases[subcanvas.id]?.last_updated;
        if (
          lastUpdated &&
          new Date(lastUpdated) > new Date(subcanvas.last_updated)
        ) {
          return;
        }

        // Do not delete the storyboards as it can sometimes get too big and not come back
        // in the snapshots
        if (
          state.subCanvases?.[subcanvas.id]?.storyboards?.length &&
          !subcanvas?.storyboards?.length
        ) {
          subcanvas.storyboards = state.subCanvases[subcanvas.id].storyboards;
        }

        // Do not delete the annotations as it can sometimes get too big and not come back
        // in the snapshots
        if (
          state.subCanvases?.[subcanvas.id]?.annotations?.length &&
          !subcanvas?.annotations?.length
        ) {
          subcanvas.annotations = state.subCanvases[subcanvas.id].annotations;
        }

        state.subCanvases[subcanvas.id] = subcanvas;
      });

      action.payload?.deletedItems?.forEach((subcanvas) => {
        delete state.subCanvases[subcanvas.id];
      });
    },
    setSubCanvasesLoading: (state) => {
      state.subCanvasesLoadStatus = "loading";
    },
    setProjectIdsByLastUpdated: (state, action) => {
      state.projectIdsByLastUpdated = action.payload;
      state.projectIdsByLastUpdatedLoadStatus = "succeeded";
    },
    setProjectIdsByLastUpdatedLoading: (state) => {
      state.projectIdsByLastUpdatedLoadStatus = "loading";
    },
    setProjects: (state, action) => {
      state.projects = {};
      action.payload?.forEach((project: { id: string }) => {
        state.projects[project.id] = project;
      });
      state.projectsLoadStatus = "succeeded";
    },
    // for setting both the canvas and the canvas preview (labels, assigned_user_id, etc)
    updateCanvasFields: (state, action) => {
      const canvas = state.canvases[action.payload.id];
      if (canvas) {
        state.canvases[action.payload.id] = {
          ...canvas,
          ...action.payload.fields,
        };
      }

      const canvasPreview = state.canvasPreviews[action.payload.id];

      if (canvasPreview) {
        state.canvasPreviews[action.payload.id] = {
          ...canvasPreview,
          ...action.payload.fields,
        };
      }
    },
    onProjectsSnapshot: (state, action) => {
      action.payload?.newItems.forEach((project: { id: string }) => {
        state.projects[project.id] = project;
      });

      action.payload?.deletedItems?.forEach((project: { id: string }) => {
        delete state.projects[project.id];
      });
    },
    setProjectsLoading: (state) => {
      state.projectsLoadStatus = "loading";
    },
    setActivities: (state, action: { payload: ActivityStream[] }) => {
      state.activityStreamList = [];
      state.activityStreamLookup = {};

      action.payload.forEach((activity) => {
        state.activityStreamList.push(activity);
        state.activityStreamLookup[activity.id] = activity;
      });

      // Not technically necessary but good to make sure
      state.activityStreamList = [
        ...state.activityStreamList.sort((a, b) =>
          b.timestamp?.localeCompare(a.timestamp)
        ),
      ];
      state.activityStreamLoadStatus = "succeeded";
    },
    onActivitySnapshot: (state, action) => {
      const updatedActivities = {};
      const deletedActivities = new Set();

      action.payload?.newItems?.forEach((activity: any) => {
        if (state.activityStreamLookup[activity["id"]]) {
          updatedActivities[activity["id"]] = activity;
        } else {
          state.activityStreamList.push(activity);
        }

        state.activityStreamLookup[activity["id"]] = activity;
      });

      action.payload?.deletedItems?.forEach((activity: any) => {
        deletedActivities.add(activity["id"]);
        delete state.activityStreamLookup[activity["id"]];
      });

      const newActivityStreamList: ActivityStream[] = [];
      state.activityStreamList.forEach((activity) => {
        if (deletedActivities.has(activity.id)) {
          return;
        }

        if (updatedActivities[activity.id]) {
          newActivityStreamList.push(updatedActivities[activity.id]);
        } else {
          newActivityStreamList.push(activity);
        }
      });

      // Not technically necessary but good to make sure
      state.activityStreamList = [...newActivityStreamList].sort((a, b) =>
        b.timestamp?.localeCompare(a.timestamp)
      );
    },
    setActivityStreamLoading: (state) => {
      state.activityStreamLoadStatus = "loading";
    },
    setActivityChangeUndone: (state, action) => {
      state.activityStreamLookup[action.payload.id].change_undone =
        action.payload.changeUndone;

      const activityIndex = state.activityStreamList.findIndex(
        (activity) => activity.id === action.payload.id
      );
      state.activityStreamList = [
        ...state.activityStreamList.slice(0, activityIndex),
        state.activityStreamLookup[action.payload.id],
        ...state.activityStreamList.slice(activityIndex + 1),
      ];
    },

    setActivityStreamDiffs: (state, action) => {
      // we don't reset activityStreamDiffs to {} because they are immutable - new ones are just appended to the state
      action.payload?.forEach((diff: any) => {
        state.activityStreamDiffs[diff["id"]] = diff;
      });
      state.activityStreamDiffsLoadStatus = "succeeded";
    },
    onActivityStreamDiffsSnapshot: (state, action) => {
      action.payload?.newItems?.forEach((diff: any) => {
        state.activityStreamDiffs[diff["id"]] = diff;
      });

      action.payload?.deletedItems?.forEach((diff: any) => {
        delete state.activityStreamDiffs[diff["id"]];
      });
    },
    setActivityStreamDiffsLoading: (state) => {
      state.activityStreamDiffsLoadStatus = "loading";
    },

    setProjectLoadingProgress: (state, action) => {
      state.projectLoadingProgress = action.payload;
      state.projectLoadingProgressLoadStatus = "succeeded";
    },
    setProjectLoadingProgressLoading: (state) => {
      state.projectLoadingProgressLoadStatus = "loading";
    },
    onProjectLoadingProgressSnapshot: (state, action) => {
      state.projectLoadingProgress = action.payload;
    },
    deleteCanvas: (state, action) => {
      const canvasId = action.payload;

      delete state.canvases[canvasId];
      delete state.canvasPreviews[canvasId];
      delete state.subCanvases[canvasId];
    },
  },
});

/**
 * Utility to update storyboards & annotations & set in redux instantly
 */
export const updateCanvasStoryboards = async (
  supabaseClient,
  dispatch: Dispatch<AnyAction>,
  newStoryboards: Storyboard[],
  canvas: Canvas | SubCanvas,
  updateTs?: Date,
  annotations?: Annotation[]
) => {
  // Don't allow updating to 0 storyboards
  if (!newStoryboards?.length) {
    return;
  }

  const last_updated = updateTs || new Date();

  const updateDict: any = {
    storyboards: newStoryboards,
    last_updated,
  };

  if (annotations) {
    updateDict.annotations = annotations;
  }

  const newItem = {
    ...canvas,
    ...updateDict,
  };

  // This is a sub-canvas
  if ((canvas as SubCanvas).canvas_id) {
    dispatch(
      onSubCanvasesSnapshot({
        newItems: [newItem],
        forceUpdate: true,
      })
    );

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", canvas.id);

    return;
  }

  dispatch(
    onCanvasesSnapshot({
      newItems: [newItem],
      forceUpdate: true,
    })
  );

  await supabaseClient.from("canvases").update(updateDict).eq("id", canvas.id);
};

export const {
  setElementMetadata,
  onElementMetadataSnapshot,
  setElementMetadataLoading,
  setProjects,
  updateCanvasFields,
  onProjectsSnapshot,
  setProjectsLoading,
  setCanvases,
  setCanvasPreviews,
  onCanvasesSnapshot,
  setCanvasesLoading,
  setSubCanvasesLoading,
  setSubCanvases,
  onSubCanvasesSnapshot,
  setIgnoreCanvasUpdates,
  setActivities,
  onActivitySnapshot,
  setActivityStreamLoading,
  setActivityStreamDiffs,
  setActivityStreamDiffsLoading,
  onActivityStreamDiffsSnapshot,
  setActivityChangeUndone,
  setProjectLoadingProgress,
  onProjectLoadingProgressSnapshot,
  setProjectLoadingProgressLoading,

  setProjectIdsByLastUpdated,
  setProjectIdsByLastUpdatedLoading,
  setFigmaContext,
  onFigmaContextSnapshot,

  deleteCanvas,

  setDSPrompt,
  onDSPromptSnapshot,
} = supabaseSlice.actions;

export default supabaseSlice.reducer;
